import { Component, ViewEncapsulation } from '@angular/core';
import { CampaignSettingsAbstractComponent } from '../campaign-settings-abstract.component';
import {
  Campaign,
  CampaignSection,
  EmailIntegration,
  EmailIntegrationStatus,
  EmailIntegrationStatusLabels,
  EmailIntegrationType,
  EmailIntegrationTypeLabels,
  EmailTemplate,
  EmailTemplateType,
  Pager,
  SMTPTransportType,
  TicketSystem,
  User,
} from '../../_models';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import {
  AlertService,
  CampaignService,
  EmailIntegrationService,
  EmailTemplateService,
  GmailService,
  LoaderService,
  SideNavService,
  TicketSystemService,
  UserService,
} from '../../_services';
import {FormBuilder, Validators} from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import {forkJoin} from "rxjs";
import {config} from "../../../config/config";

@Component({
  templateUrl: './campaign-email-setup.component.html',
  styleUrls: ['../campaign-section.scss', './campaign-email-setup.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CampaignEmailSetupComponent extends CampaignSettingsAbstractComponent {
  public section = CampaignSection.EmailSetup;
  matchType = EmailTemplateType.Match;
  noMatchType = EmailTemplateType.NoMatch;
  matchTemplates: EmailTemplate[] = [];
  noMatchTemplates: EmailTemplate[] = [];
  useSameBodyIfNoMatch = true;
  ticketSystems: TicketSystem[] = [];
  emailIntegrations: EmailIntegration[] = [];
  show_session_start_ticket_delay: boolean = false;
  show_session_incomplete_ticket_delay: boolean = false;
  public user: User = null;
  public smtpEnabled: boolean = false;
  public showPassword: boolean = false;
  public transportTypes: { value: number; label: string }[] = [
    { value: SMTPTransportType.TLS, label: 'TLS' },
    { value: SMTPTransportType.SSL, label: 'SSL' },
    { value: SMTPTransportType.PlainText, label: 'Plain Text' },
  ];

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected campaignService: CampaignService,
    protected alertService: AlertService,
    protected formBuilder: FormBuilder,
    protected loaderService: LoaderService,
    protected sideNav: SideNavService,
    protected emailService: EmailTemplateService,
    protected ticketSystemService: TicketSystemService,
    protected emailIntegrationService: EmailIntegrationService,
    protected gmailService: GmailService,
    protected userService: UserService
  ) {
    super(router, location, route, campaignService, sideNav, alertService, loaderService);
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      template_match: [null, Validators.required],
      template_no_match: [null],
      email_forward_delay: [null, [Validators.required, Validators.min(1), Validators.max(360)]],
      ticket_system: [null],
      session_start_ticket_delay: [0],
      session_incomplete_ticket_delay: [0],
      smtp_config: this.formBuilder.group({
        host: [null],
        port: [587, [Validators.min(1), Validators.max(99999)]],
        username: [null],
        password: [null],
        mail_from: [null],
        transport_type: [SMTPTransportType.TLS],
      }),
    });
    super.ngOnInit();

    this.userService.getCurrent().subscribe(
      (user: User) => {
        this.user = user;
      },
      (error) => {
        this.handleError(error);
      }
    );
  }

  onCampaignFetch(campaign: Campaign) {
    let selectedTemplateMap = {};
    this.loaderService.show();
    this.smtpEnabled = !!(campaign.smtp_config && campaign.smtp_config.host);
    this.form.patchValue({
      email_enabled: campaign.email_enabled,
      email_forward_delay: campaign.email_forward_delay,
      ticket_system: campaign.ticket_system ? campaign.ticket_system.id : null,
      session_start_ticket_delay: campaign.session_start_ticket_delay ? campaign.session_start_ticket_delay / 3600 : 0,
      session_incomplete_ticket_delay: campaign.session_incomplete_ticket_delay
        ? campaign.session_incomplete_ticket_delay / 3600
        : 0,
      smtp_config: campaign.smtp_config || {
        host: null,
        port: 587,
        username: null,
        password: null,
        mail_from: null,
        transport_type: SMTPTransportType.TLS,
      },
    });

    this.show_session_start_ticket_delay = this.form.value.session_start_ticket_delay > 0;
    this.show_session_incomplete_ticket_delay = this.form.value.session_incomplete_ticket_delay > 0;

    //build a map of the campaign's selected templates
    for (let template_id of campaign.email_templates) {
      selectedTemplateMap[template_id] = true;
    }
    this.emailIntegrationService.setCampaignId(campaign.id);
    this.gmailService.setCampaignId(campaign.id);

    forkJoin([
      this.emailService.list({
        page: 1,
        page_size: config.maxPageSize,
        type__in: [EmailTemplateType.Match, EmailTemplateType.NoMatch].join(','),
      }),
      this.ticketSystemService.list({ page: 1, page_size: config.maxPageSize }),
      this.emailIntegrationService.list({ page: 1, page_size: config.maxPageSize }),
    ]).subscribe(
      (data: [Pager, Pager, Pager]) => {
        if (data.length > 0) {
          let emailTemplates: EmailTemplate[] = data[0].results;

          //list of all templates returned
          if (emailTemplates && emailTemplates.length) {
            for (let template of emailTemplates) {
              if (template.type == EmailTemplateType.Match) {
                this.matchTemplates.push(template);

                if (template.id in selectedTemplateMap) {
                  //matched template is selected (we only support 1 selection for now)
                  this.form.patchValue({ template_match: template.id });
                }
              } else if (template.type == EmailTemplateType.NoMatch) {
                this.noMatchTemplates.push(template);

                if (template.id in selectedTemplateMap) {
                  //unmatched template is selected (we only support 1 selection for now)
                  this.form.patchValue({ template_no_match: template.id });
                  this.useSameBodyIfNoMatch = false;
                }
              }
            }
          }
          this.ticketSystems = data[1].results;
          this.emailIntegrations = data[2].results;
        }
        this.loaderService.hide();
      },
      (error) => {
        this.handleError(error);
        this.loaderService.hide();
      }
    );
  }

  enableSameBodyIfNoMatch(event) {
    this.useSameBodyIfNoMatch = event.target.checked;
  }

  deleteTemplate(id) {
    if (window.confirm('Are you sure you want to delete this email template?')) {
      this.loading = true;

      this.emailService
        .delete(id)
        .pipe(takeUntil(this._destroy$))
        .subscribe(
          (result) => {
            this.alertService.success('Deleted email template.');
            this.loading = false;
            this.matchTemplates = this.matchTemplates.filter((data) => data.id != id);
            this.noMatchTemplates = this.noMatchTemplates.filter((data) => data.id != id);
          },
          (error) => {
            this.handleSubmitError(error);
            this.loading = false;
          }
        );
    }
  }

  deleteIntegration(id, integration_type: EmailIntegrationType) {
    const integrationToServiceMapping = {
      [EmailIntegrationType.Forwarding]: this.emailIntegrationService,
      [EmailIntegrationType.Gmail]: this.gmailService,
    };
    if (window.confirm('Are you sure you want to delete this email integration?')) {
      this.loaderService.show()
      const service = integrationToServiceMapping[integration_type];
      service
        .delete(id)
        .pipe(takeUntil(this._destroy$))
        .subscribe(
          (result) => {
            this.alertService.success('Deleted email integration.');
            this.loaderService.hide()
            this.emailIntegrations = this.emailIntegrations.filter((data) => data.id !== id);
          },
          (error) => {
            this.handleSubmitError(error);
            this.loaderService.hide()
          }
        );
    }
  }

  getIntegrationTypeLabel(type: EmailIntegrationType) {
    return EmailIntegrationTypeLabels[type];
  }

  getIntegrationStatusLabel(status: EmailIntegrationStatus) {
    return EmailIntegrationStatusLabels[status];
  }

  onTicketSystemChange(event: any) {
    const selectedValue = event.target.value;

    if (selectedValue === 'new') {
      this.router.navigate(['/ticket-system/new']);
    }
  }

  protected getFormData() {
    let selectedTemplates = [this.form.value.template_match];
    let smtpConfig = this.form.value.smtp_config;

    if (!this.smtpEnabled) {
      smtpConfig = null;
    }

    //get the selected email templates
    if (!this.useSameBodyIfNoMatch && this.form.value.template_no_match) {
      selectedTemplates.push(this.form.value.template_no_match);
    }

    return {
      email_templates: selectedTemplates,
      email_enabled: this.form.value.email_enabled,
      email_forward_delay: this.form.value.email_forward_delay,
      ticket_system: this.form.value.ticket_system,
      session_start_ticket_delay:
        this.form.value.session_start_ticket_delay && this.show_session_start_ticket_delay
          ? this.form.value.session_start_ticket_delay * 3600
          : 0,
      session_incomplete_ticket_delay:
        this.form.value.session_incomplete_ticket_delay && this.show_session_incomplete_ticket_delay
          ? this.form.value.session_incomplete_ticket_delay * 3600
          : 0,
      smtp_config: smtpConfig,
    };
  }

  canEditEmailIntegration(integrationType: EmailIntegrationType) {
    return integrationType === EmailIntegrationType.Forwarding;
  }
}
